import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import httpInjectorService from 'services/http-injector.service';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { Empty } from 'antd';
import { BulletList } from 'react-content-loader';
import { Badge } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setEmployeesMonthlyLogs } from 'store/actions';
import { decryptData } from 'utils/crypto';
import TableContainer from 'components/common/TableContainer';
import { FaDownload } from 'react-icons/fa';
import { FiFilter } from 'react-icons/fi';
import { EnvironmentOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { demoEmployeehistLogs } from 'components/demodata/Dummydata';
import LocationModal from './LocationModal';

// Helper function to convert epoch time to IST
const epochToIST = (epochTime) => {
  const date = new Date(epochTime * 1000); // Convert seconds to milliseconds
  return date.toLocaleTimeString('en-IN', {
    hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

// Helper function to format the date without time
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
};

// Helper function to format date to local date (YYYY-MM-DD)
const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = `0${date.getMonth() + 1}`.slice(-2);
  const day = `0${date.getDate()}`.slice(-2);
  return `${year}-${month}-${day}`;
};

const EmployeeHistoricalLog = ({ activeTab }) => {
  // const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [display, setDisplay] = useState(true);
  const [dateRange, setDateRange] = useState({ fromDate: null, toDate: null });

  const [searchQuery, setSearchQuery] = useState('');

  const dispatch = useDispatch();

  const roleId = decryptData(Cookies.get('role_id'));

  const employees = useSelector(
    (state) => state.Authentication.employeesMonthlyLogs,
  );

  const [locationModal, setLocationModal] = useState({
    open: false,
    latitude: null,
    longitude: null,
    title: '',
  });

  const fetchData = async (dateRange) => {
    setLoading(true);
    try {
      const isDemoMode = localStorage.getItem('demoMode') === 'true';

      if (isDemoMode) {
        dispatch(setEmployeesMonthlyLogs(demoEmployeehistLogs));
        setLoading(false);
        return;
      }
      const formattedRange = {
        fromDate: dateRange.fromDate
          ? formatLocalDate(dateRange.fromDate)
          : null,
        toDate: dateRange.toDate ? formatLocalDate(dateRange.toDate) : null,
      };
      const response = await httpInjectorService.monthlylogs(formattedRange);

      if (response?.data) {
        const updatedData = response.data.map((Employee, index) => ({
          ...Employee,
          id: Employee.id || index,
          formatted_date: formatDate(Employee.date),
          formattedClockIn: Employee.firstclockin
            ? epochToIST(Employee.firstclockin)
            : '--',
          formattedClockOut: Employee.lastclockout
            ? epochToIST(Employee.lastclockout)
            : '--',
        }));
        dispatch(setEmployeesMonthlyLogs(updatedData));
        // setEmployees(updatedData);
        setLoading(false);
      } else {
        setLoading(false);
        setDisplay(false);
        dispatch(setEmployeesMonthlyLogs([]));
      }
    } catch (error) {
      console.error('Error fetching employee data:', error);
      setLoading(false);
      setDisplay(false);
      dispatch(setEmployeesMonthlyLogs([]));
    }
  };

  const handleFilter = async () => {
    if (dateRange.fromDate && dateRange.toDate) {
      setLoading(true);
      fetchData(dateRange);
    } else {
      toast.warning('Please select both from and to dates', {
        position: 'top-right',
      });
    }
  };

  const handleDownload = async () => {
    try {
      const payload = {
        start: dateRange.fromDate,
        end: dateRange.toDate,
      };

      const response =
        await httpInjectorService.downloadHistoricalsLog(payload);
      // console.log(response, 'response');

      if (response.status === 'success' && response.data?.length > 0) {
        const data = response.data.map((row) => {
          const clockIn =
            row.CLOCK_IN && row.CLOCK_IN !== 0
              ? new Date(row.CLOCK_IN * 1000).toLocaleString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                })
              : '';

          const clockOut =
            row.CLOCK_OUT && row.CLOCK_OUT !== 0
              ? new Date(row.CLOCK_OUT * 1000).toLocaleString('en-IN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                  hour12: false,
                })
              : '';

          const date = new Date(row.date).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });

          return {
            EMPLOYEE_ID: row.EMPLOYEE_ID,
            NAME: row.NAME,
            DATE: date,
            CLOCK_IN: clockIn,
            CLOCK_OUT: clockOut,
            STATUS: row.STATUS,
            TOTAL_HOURS: row.TOTAL_HOURS,
          };
        });

        // Convert JSON to CSV
        const headers = Object.keys(data[0]).join(',');
        const rows = data
          .map((row) =>
            Object.values(row)
              .map((value) => `"${value}"`)
              .join(','),
          )
          .join('\n');

        const csvContent = [headers, rows].join('\n');

        // Trigger CSV download
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'historical_logs.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('File Downloaded', {
          position: 'top-right',
          autoClose: 2000,
        });
      } else {
        toast.error('No data available for download!', {
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Download failed!', { position: 'top-right' });
    }
  };

  useEffect(() => {
    if (activeTab === '4') {
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 2);
      setDateRange({
        fromDate: thirtyDaysAgo,
        toDate: today,
      });
      fetchData({
        fromDate: thirtyDaysAgo,
        toDate: today,
      });
    }
  }, [activeTab]);

  const filteredEmployees = employees.filter((employee) => {
    if (roleId === 2 || roleId === 1) {
      const name = employee?.Name?.toLowerCase() || '';
      return name.includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // const convertToInputTime = (timeStr) => {
  //   if (!timeStr || timeStr === '--') return '';

  //   const [time, modifier] = timeStr.split(' '); // "02:30 PM" -> ["02:30", "PM"]
  //   let [hours, minutes] = time.split(':');

  //   if (modifier.toUpperCase() === 'PM' && hours !== '12') {
  //     hours = parseInt(hours, 10) + 12;
  //   }
  //   if (modifier.toUpperCase() === 'AM' && hours === '12') {
  //     hours = '00';
  //   }

  //   return `${hours.toString().padStart(2, '0')}:${minutes}`;
  // };

  // const convertTo12Hour = (time24) => {
  //   let [hours, minutes] = time24.split(':');
  //   let period = 'AM';
  //   hours = parseInt(hours, 10);
  //   if (hours >= 12) {
  //     period = 'PM';
  //     if (hours > 12) hours -= 12;
  //   }
  //   if (hours === 0) hours = 12;
  //   return `${hours.toString().padStart(2, '0')}:${minutes} ${period}`;
  // };

  const openLocationModal = (lat, lng, title) => {
    setLocationModal({
      open: true,
      latitude: lat,
      longitude: lng,
      title,
    });
  };

  const closeLocationModal = () => {
    setLocationModal({
      open: false,
      latitude: null,
      longitude: null,
      title: '',
    });
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: 'formatted_date',
      },
      {
        Header: 'Employee ID',
        accessor: 'employee_id',
      },
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Clock In',
        accessor: 'formattedClockIn',
      },
      {
        Header: 'Clock Out',
        accessor: 'formattedClockOut',
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => {
          let color =
            value === 'Present'
              ? 'success'
              : value === 'Absent'
                ? 'danger'
                : value === 'Holiday' || value === 'Weekend'
                  ? 'warning'
                  : value === 'OnLeave'
                    ? 'primary'
                    : 'secondary';

          return <Badge color={color}>{value}</Badge>;
        },
      },
      {
        Header: 'Total Hours',
        accessor: 'totalhours',
      },
      {
        Header: 'Clock In Location',
        Cell: ({ row }) => {
          const { firstclockin, clock_in_latitude, clock_in_longitude } =
            row.original;

          if (!firstclockin || !clock_in_latitude || !clock_in_longitude) {
            return '-';
          }

          return (
            <Tooltip title="View Clock In Location">
              <EnvironmentOutlined
                style={{ cursor: 'pointer', fontSize: 18 }}
                onClick={() =>
                  openLocationModal(
                    clock_in_latitude,
                    clock_in_longitude,
                    'Clock In Location',
                  )
                }
              />
            </Tooltip>
          );
        },
      },
      {
        Header: 'Clock Out Location',
        Cell: ({ row }) => {
          const { lastclockout, clock_out_latitude, clock_out_longitude } =
            row.original;

          if (!lastclockout || !clock_out_latitude || !clock_out_longitude) {
            return '-';
          }

          return (
            <Tooltip title="View Clock Out Location">
              <EnvironmentOutlined
                style={{ cursor: 'pointer', fontSize: 18 }}
                onClick={() =>
                  openLocationModal(
                    clock_out_latitude,
                    clock_out_longitude,
                    'Clock Out Location',
                  )
                }
              />
            </Tooltip>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <div
        className="page-content"
        style={{ borderRadius: '10px', marginTop: '20px' }}
      >
        <div className="container-fluid">
          <div class="d-flex justify-content-end">
            <DatePicker
              selected={dateRange.fromDate}
              onChange={(date) =>
                setDateRange({ ...dateRange, fromDate: date })
              }
              selectsStart
              startDate={dateRange.fromDate}
              endDate={dateRange.toDate}
              placeholderText="From Date"
            />
            <DatePicker
              selected={dateRange.toDate}
              onChange={(date) => setDateRange({ ...dateRange, toDate: date })}
              selectsEnd
              startDate={dateRange.fromDate}
              endDate={dateRange.toDate}
              minDate={dateRange.fromDate}
              placeholderText="To Date"
            />
            <Button
              size="sm"
              rounded={2}
              colorScheme="purple"
              onClick={handleFilter}
            >
              <FiFilter />
              &nbsp; Filter
            </Button>
            <Button
              rounded={2}
              size="sm"
              colorScheme="purple"
              onClick={handleDownload}
              style={{ marginLeft: '10px' }}
            >
              <FaDownload />
              &nbsp; Download
            </Button>
          </div>
          {loading ? (
            <div className="text-center">
              <BulletList />
            </div>
          ) : employees.length === 0 ? (
            <div className="mt-5">
              <Empty />
            </div>
          ) : (
            <div>
              {employees.length > 0 && (
                <>
                  <TableContainer
                    columns={columns}
                    data={filteredEmployees}
                    isGlobalFilter={true}
                    customPageSize={10}
                    className="custom-header-css"
                  />

                  <LocationModal
                    open={locationModal.open}
                    onClose={closeLocationModal}
                    latitude={locationModal.latitude}
                    longitude={locationModal.longitude}
                    title={locationModal.title}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeHistoricalLog;
